import axios from 'axios';
import moment from 'moment';
import Cookies from 'js-cookie';
import { getServicesErrorObj } from '../servicesDowntime/actions';
import {
  REFER_CLIENT_REQUEST_START,
  REFER_CLIENT_REQUEST_FAIL,
  REFER_CLIENT_REQUEST_SUCCESS,
  ETV_OPTION_REQUEST_START,
  ETV_OPTION_REQUEST_FAIL,
  ETV_OPTION_REQUEST_SUCCESS,
  REFER_AFFILIATE_REQUEST_START,
  REFER_AFFILIATE_REQUEST_FAIL,
  REFER_AFFILIATE_REQUEST_SUCCESS
} from './types';
import {
  CFX, ORGANIZATION__CODE_MAPPING,
  REFERRED_AFFILIATE_JOURNEY,
  REFERRED_CLIENT_TYPES,
  DOMAIN_COMPANY_MAP
} from '../../../core/utils/Constants/Constants';
import { getReferringCompany } from '../../../core/utils/ReferringCompanyValueGetter/ReferringCompanyValueGetter';
import { post } from './../session/actions';
import { BRANCH_NAME_MAPPING, CDSA_REFERRING_VALUE } from '../../../core/utils/Constants/Constants';
import GetBasicDetails from '../../../core/utils/GetBasicDetails';
import { Affiliate } from '../../../core/types/AffiliateDetails';
import { APStepContainerFormForReferrals } from '../../../core/types/APStepContainerTypes';
import { getConfigProperty } from '../../../core/EnvironmentConfig/EnvironmentConfig';

type etvApiPayload = {
  org_code: string,
  legal_entity: string
}

const getEnvironment = (hostname: string): keyof typeof DOMAIN_COMPANY_MAP => {
  if (hostname.includes('.sit.')) return 'SIT';
  if (hostname.includes('.uat.')) return 'UAT';
  return 'PROD';
};

const setCdhCompanyCookie = (company: string) => {
  Cookies.set('cdhCompany', company, { path: '/', sameSite: 'none', secure: true });
};

export const referClientRequestStart = () => ({
  type: REFER_CLIENT_REQUEST_START
});

export const referClientRequestFail = () => ({
  type: REFER_CLIENT_REQUEST_FAIL
});

export const referClientRequestSuccess = (payload: any) => ({
  type: REFER_CLIENT_REQUEST_SUCCESS,
  payload
});

export const referAffiliateRequestStart = () => ({
  type: REFER_AFFILIATE_REQUEST_START
});

export const referAffiliateRequestFail = () => ({
  type: REFER_AFFILIATE_REQUEST_FAIL
});

export const referAffiliateRequestSuccess = (payload: any) => ({
  type: REFER_AFFILIATE_REQUEST_SUCCESS,
  payload
});

export const etvOptionRequestStart = () => ({
  type: ETV_OPTION_REQUEST_START
});

export const etvOptionRequestFail = () => ({
  type: ETV_OPTION_REQUEST_FAIL
});

export const etvOptionRequestSuccess = (payload: etvApiPayload) => ({
  type: ETV_OPTION_REQUEST_SUCCESS,
  payload
});

const sandBoxAPIKey: string = getConfigProperty('apiKey')['sandbox'];

const REFER_CLIENT_URL: string = `/v5/webleads?apikey=${sandBoxAPIKey}`;
const REFER_AFFILIATE_URL: string = getConfigProperty('apiEndPoint')['createPartner'] as string;

const LEAD_FAIL_NOTIFICATION_URL: string = `/v1/affiliate-portal/fail-notification`;
const ETV_OPTION_URL: string = `/v1/affiliate-portal/affiliates/get-etv`;

const updateClientPhone = (client: any) => {
  const { phone } = client;

  if (phone?.select.length === 0 && phone?.input.length === 0) {
    client.phone = client.mobile;
  }
  return client;
};

const setCommissionOptOutDate = (client: any) => {
  if (client?.commission_opt_out) {
    client.commission_opt_out = moment().utc().format('YYYY-MM-DD');
  } else {
    client.commission_opt_out = null;
  }
  return client;
};

const buildPacket = (client: any, affiliateNumber: string | null, cdhCompany: string, branch_name: string) => {
  const { best_time_to_call, companyName, country, customerType, email, enquiry_info, est_transaction_value, firstName, lastName, language,
    salutation, trading_name } = client;

  const packet = {
    lead_type: 'Enquiry',
    service_required: 'Transfer',
    cdh_company: getReferringCompany() === CDSA_REFERRING_VALUE ? "CD SA" : cdhCompany,
    channel: 'Affiliate Portal',
    source: 'Affiliate',
    sub_source: 'Other',
    enquiry_method: 'Affiliate',
    afflNo: affiliateNumber,
    branch_name,
    mobile: `${client.mobile.select}-${client.mobile.input}`,
    phone: `${client.phone.select}-${client.phone.input}`,
    currency_pair: `${client.sellingCurrency}${client.buyingCurrency}`,
    best_time_to_call,
    company: companyName,
    country,
    cust_type: customerType,
    email,
    enquiry_info,
    est_transaction_value,
    fname: firstName,
    lname: lastName,
    language_preference: language,
    salutation,
    trading_name,
    ...(client.enquiry_info && { enquiry_notes: '' })
  };

  return packet;
};

export const referClient = (client: APStepContainerFormForReferrals, affiliate: Affiliate) => (
  dispatch: Function
) => new Promise((resolve, reject) => {
  dispatch(referClientRequestStart());

  const affiliateNumber: string | null = (sessionStorage.getItem('affiliate-number'));
  let { organisationCode: cdhCompany } = GetBasicDetails();
  const branchName = BRANCH_NAME_MAPPING[getReferringCompany()];

  client = setCommissionOptOutDate(updateClientPhone(client));

  const packet = buildPacket(client, affiliateNumber, cdhCompany, branchName);
  const date = moment(new Date()).utc().format('DDMMMYYYY');
  const payload = {
    "org_code": cdhCompany,
    "legal_entity": affiliate.legal_entity,
    "affiliate_name": `${affiliate.user_first_name} ${affiliate.user_last_name}`,
    "customer_name": `${client.firstName} ${client.lastName}`,
    "affiliate_type": client.customerType,
    ...(client.customerType === CFX && { "company_name": client.companyName }),
    "date": date,
    "sf_affiliate_id": affiliate.SF_ID,
    "account_manager_email": affiliate.owner_emailid
  }

  axios.post(REFER_CLIENT_URL, {
    ...packet
  }).then((response) => {
    dispatch(referClientRequestSuccess(response));
    resolve(response.data);
  }).catch((error: any) => {
    dispatch(referClientRequestFail());
    const leadFailPayload = {
      ...payload,
    };
    dispatch(
      post(LEAD_FAIL_NOTIFICATION_URL, {
        ...leadFailPayload
      })
    );
    dispatch(getServicesErrorObj(error));
    reject(error);
  });
});

export const getETVDetails = (legalEntity: string) => (
  dispatch: Function
) => new Promise((resolve, reject) => {
  dispatch(etvOptionRequestStart());

  let referringCompany = getReferringCompany();

  // Add validation to prevent undefined org_code
  if (!referringCompany || referringCompany === 'undefined') {
    Cookies.remove('cdhCompany', { path: '/', sameSite: 'none', secure: true });
    const hostname = window.location.hostname;
    const env = getEnvironment(hostname);
    const envMap = DOMAIN_COMPANY_MAP[env];

    referringCompany = envMap[hostname] ?? 'currenciesdirect';

    if (referringCompany) {
      setCdhCompanyCookie(referringCompany);
    }
  }

  const ORGANISATION = ORGANIZATION__CODE_MAPPING[referringCompany];


  const requestBody = {
    "org_code": ORGANISATION,
    "legal_entity": legalEntity
  };

  dispatch(post(ETV_OPTION_URL, requestBody)).then((response: any) => {
    resolve(response);
    dispatch(etvOptionRequestSuccess(response.data));
  }).catch((error: any) => {
    dispatch(etvOptionRequestFail());
    dispatch(getServicesErrorObj(error));
    reject(error);
  });
});

export type ReferClientAction = {
  type: string,
  payload?: any
};

export const referAffiliate = (client: APStepContainerFormForReferrals, affiliate: Affiliate) => (
  dispatch: Function
) => new Promise((resolve, reject) => {
  dispatch(referAffiliateRequestStart());

  client = setCommissionOptOutDate(client);

  const {
    basicAuthToken: BASIC_AUTH_TOKEN,
    organisationCode: ORGANISATION
  } = GetBasicDetails();

  const {
    phone,
    mobile,
    email,
    salutation,
    firstName,
    lastName,
    companyName,
    best_time_to_call,
    nature_of_business,
    website,
    enquiry_info,
    is_referral_agreed_to_share_contact_info,
    commission_opt_out,
    client_type } = client;

  const { affiliate_Number } = affiliate;
  const clientType: string = client_type ? REFERRED_CLIENT_TYPES[client_type] : '';
  const affiliateNumber: string | null = (sessionStorage.getItem('affiliate-number'));

  let payLoad = {
    "clientdata": {
      "client_type": clientType,
      "client_languages": '',
      "client_industry": '',
      "client_based_countries": '',
      "account_manager_emails": false,
      "business_sector": '',
      "business_category": ''
    },
    "affdetails":
    {
      "referral_text": "internet",
      "organisation": ORGANISATION,
      "salutation": salutation,
      "company": companyName ? companyName : '',
      "firstname": firstName,
      "lastname": lastName,
      "phone": (mobile.select && mobile.input) ? `${mobile.select}-${mobile.input}` : "",
      "landline": (phone.select && phone.input) ? `${phone.select}-${phone.input}` : "",
      "country": '',
      "postalcode": '',
      "street": "",
      "city": "",
      "state": '',
      "email": email.trim(),
      "password": '',
      "best_time_to_call": best_time_to_call || '',
      "nature_of_business": nature_of_business,
      "website": website || '',
      "description": enquiry_info || '',
      "is_referral_agreed_to_share_contact_info": is_referral_agreed_to_share_contact_info,
      "commission_opt_out": commission_opt_out,
      "referring_aff_tan_no": affiliate_Number,
      "partnerflowtype": REFERRED_AFFILIATE_JOURNEY
    }
  }

  axios.post(REFER_AFFILIATE_URL, payLoad, {
    headers: {
      "Access-Control-Allow-Origin": "*",
      'Content-Type': 'application/json',
      'Authorization': `Basic ${BASIC_AUTH_TOKEN}`,
      "affiliate_number": affiliateNumber
    }
  })
    .then((response) => {
      dispatch(referClientRequestSuccess(response));
      resolve(response.data);
    }).catch((error: any) => {
      dispatch(referAffiliateRequestFail());
      dispatch(getServicesErrorObj(error));
      reject(error);
    });
});